import { GET_DAILY_LOWEST_PRICES } from "@/actions/GET_DAILY_LOWEST_PRICES.ts";
import { useGetAccommodationTypes } from "@/actions/hooks/useGetAccommodationTypes.tsx";
import { DayPriceMap, useDayPriceStore } from "@/components/Inputs/RangePicker/stores/useDayPriceStore.ts";
import { DailyPrice, QueryDailyLowestPricesArgs } from "@/helpers/types/types.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { useQuery } from "@apollo/client";
import { addDays, addMonths, endOfMonth, startOfMonth } from "date-fns";
import format from "date-fns/format";
import { useEffect, useMemo, useState } from "react";

interface UseDailyLowestPricesParams {
  /** Left-most month currently displayed by the day picker. */
  displayedMonth: Date;
}

interface GetDailyLowestPricesResponse {
  dailyLowestPrices: DailyPrice[];
}

const FETCH_DEBOUNCE_MS = 250;

const buildDayPriceMap = (dailyPrices: DailyPrice[]): DayPriceMap =>
  dailyPrices.reduce<DayPriceMap>((map, { date, price }) => {
    // `date` is the raw "YYYY-MM-DD" string from the API, matching DayButton's local day key.
    if (price?.withDiscount != null) {
      map[date] = price.withDiscount;
    }
    return map;
  }, {});

/** Debounces a value, so paging quickly through months doesn't fan out a burst of requests. */
const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeoutId);
  }, [value, delayMs]);

  return debounced;
};

/**
 * Fetches the lowest price per day for the two months around `displayedMonth` and writes
 * them into the shared day-price store (read by DayButton). It runs regardless of whether
 * the picker is open, so the initial months are prefetched and already present on first open.
 *
 * Prices are scoped to a single accommodation type. The store's `accommodationTypeId`
 * actually holds the type *code* (see PickingHeader), while `dailyLowestPrices` needs the
 * real ID — so we resolve it here, falling back to the only type when there is exactly one.
 */
export const useDailyLowestPrices = ({ displayedMonth }: UseDailyLowestPricesParams) => {
  const { accommodationTypes } = useGetAccommodationTypes();
  const accommodationTypeCode = useEngineStore((state) => state.accommodationTypeId);
  const setDayPrices = useDayPriceStore((state) => state.setDayPrices);
  const setPricesLoading = useDayPriceStore((state) => state.setPricesLoading);

  const effectiveAccommodationTypeId = useMemo(() => {
    if (!accommodationTypes.length) return undefined;
    if (accommodationTypeCode) {
      return accommodationTypes.find((type) => type.code === accommodationTypeCode)?.id;
    }
    if (accommodationTypes.length === 1) return accommodationTypes[0].id;
    return undefined;
  }, [accommodationTypes, accommodationTypeCode]);

  const debouncedMonth = useDebouncedValue(displayedMonth, FETCH_DEBOUNCE_MS);
  const isValidMonth = !Number.isNaN(debouncedMonth.getTime());
  const skip = !effectiveAccommodationTypeId || !isValidMonth;

  const { data, loading } = useQuery<GetDailyLowestPricesResponse, QueryDailyLowestPricesArgs>(
    GET_DAILY_LOWEST_PRICES,
    {
      skip,
      fetchPolicy: "cache-first",
      variables: {
        accommodationTypeId: effectiveAccommodationTypeId ?? "",
        // Guarded against an invalid date (skip is true in that case, so these aren't sent).
        from: isValidMonth ? format(startOfMonth(debouncedMonth), "yyyy-MM-dd") : "",
        // `until` is exclusive (checkout-style), so request one day past the last visible day
        // to include its price; the response still covers exactly the two visible months.
        until: isValidMonth ? format(addDays(endOfMonth(addMonths(debouncedMonth, 1)), 1), "yyyy-MM-dd") : "",
      },
      onError: (error) => console.error("GET_DAILY_LOWEST_PRICES failed:", error),
    },
  );

  // Show skeletons while a fetch is in flight, and during the debounce gap right after the
  // user navigates to a new month (so cells don't briefly show the previous month's prices).
  const isStale = debouncedMonth.getTime() !== displayedMonth.getTime();
  useEffect(() => {
    setPricesLoading(!skip && (loading || isStale));
  }, [skip, loading, isStale, setPricesLoading]);

  // Replace the whole map so prices never linger across type/range changes.
  useEffect(() => {
    if (data?.dailyLowestPrices) {
      setDayPrices(buildDayPriceMap(data.dailyLowestPrices));
    }
  }, [data, setDayPrices]);

  // No resolvable type → clear any prices left over from a previously-selected type.
  useEffect(() => {
    if (!effectiveAccommodationTypeId) {
      setDayPrices({});
    }
  }, [effectiveAccommodationTypeId, setDayPrices]);
};
